import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { lastValueFrom } from 'rxjs';

/**
 * An adapter that allows the Angular HttpClient to be used as a replacement for the global `fetch` function.
 * This is used to supply a custom fetch function to the apollo-upload-client whilst also allowing the
 * use of Angular's http infrastructure such as interceptors.
 */
@Injectable({
    providedIn: 'root'
})
export class FetchAdapter {

    private httpClient = inject(HttpClient);

    fetch = async (input: Request | string, init: RequestInit): Promise<Response> => {
        const url = typeof input === 'string' ? input : input.url;
        const method = typeof input === 'string' ? (init.method ? init.method : 'GET') : input.method;

        return lastValueFrom(
            this.httpClient.request(method, url, {
                body: init.body,
                headers: init.headers as any,
                observe: 'response',
                responseType: 'json',
                withCredentials: true,
            }),
        ).then(result => new Response(JSON.stringify(result.body), {
            status: result.status,
            statusText: result.statusText,
        })).catch((error: unknown) => {
            console.error('Error in fetch adapter', error);
            // Native fetch() rejects on network failure (HttpClient status 0).
            // Do not map status 0 to 503: PHX uses 503 for restore maintenance,
            // bootstrap, and nginx upstream-unavailable. A fake 503 looks like
            // maintenance instead of "the browser never got a response".
            if (error instanceof HttpErrorResponse && error.status === 0) {
                throw error;
            }
            return this.toFetchResponse(error);
        });
    };

    /**
     * HttpClient throws on 4xx/5xx. Preserve the real status so Apollo does not report a fake 500
     * (e.g. nginx 413 when a backup upload exceeds client_max_body_size).
     */
    private toFetchResponse(error: unknown): Response {
        if (error instanceof HttpErrorResponse && error.status > 0) {
            return new Response(this.serializeHttpErrorBody(error.error), {
                status: error.status,
                statusText: error.statusText || 'Error',
            });
        }
        return new Response(null, { status: 500, statusText: 'Internal Server Error' });
    }

    private serializeHttpErrorBody(errorBody: unknown): string | null {
        if (errorBody == null || errorBody instanceof ProgressEvent) {
            return null;
        }
        if (typeof errorBody === 'string') {
            return errorBody;
        }
        try {
            return JSON.stringify(errorBody);
        } catch {
            return null;
        }
    }
}
